import React, { useState } from 'react'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { Button } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ModelListPage, { FILTER_TYPE } from 'app/components/ModelListPage'
import Select from 'app/components/reusable/Select'
import { useDismissModal, useModal } from 'app/ducks/modal/hooks'
import ActivityClassRollCall from '../gym/ActivityClassRollCall'
import { printAttendanceList } from './printAttendanceList'

export const ACTIVITY_CLASS_FILTERS = [
  {
    key: 'is_active',
    label: 'Situação',
    icon: 'toggle_on',
    type: FILTER_TYPE.BOOLEAN,
    trueLabel: 'Ativas',
    falseLabel: 'Inativas',
  },
  {
    key: 'modalidade',
    param: 'activity_modality',
    label: 'Modalidade',
    icon: 'sports',
    type: FILTER_TYPE.MODEL,
    multiple: true,
    djangoApp: 'activities',
    djangoModel: 'activity-modality',
  },
  {
    key: 'professor',
    param: 'teacher',
    label: 'Professor',
    icon: 'person',
    type: FILTER_TYPE.MODEL,
    multiple: true,
    djangoApp: 'users',
    djangoModel: 'user',
  },
  {
    key: 'espaco',
    param: 'physical_space',
    label: 'Espaço',
    icon: 'deck',
    type: FILTER_TYPE.MODEL,
    multiple: true,
    djangoApp: 'physical-spaces',
    djangoModel: 'physical-space',
  },
]

const useStyles = makeStyles(({ palette }) => ({
  muted: { color: palette.text.secondary, fontSize: 12 },
}))

const formatWeekDays = (weekDays) =>
  weekDays
    ?.map((weekDay) =>
      moment()
        .day(weekDay + 1 || 0)
        .format('ddd')
    )
    .join(', ')

const cycleOptions = () =>
  Array(12)
    .fill(0)
    .map((_, idx) => {
      const cycle = moment()
        .add(idx - 6, 'month')
        .format('YYYYMM')
      return { label: cycle, value: cycle }
    })

// Asks for the cycle of the attendance lists to print.
const AttendanceCycleForm = ({ description, onConfirm }) => {
  const [cycle, setCycle] = useState(moment().format('YYYYMM'))

  return (
    <>
      <p className="mt-0">{description}</p>
      <Select
        fullWidth
        id="attendance-cycle"
        name="attendance-cycle"
        label="Ciclo"
        value={cycle}
        className="mb-3"
        onChange={(e) => setCycle(e.target.value)}
        options={cycleOptions()}
      />
      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={() => onConfirm(cycle)}
      >
        Imprimir
      </Button>
    </>
  )
}

const ClassCell = ({ activityClass }) => {
  const classes = useStyles()
  return (
    <div>
      <b className="text-primary">
        <Link
          to={`/activity-class-details/${activityClass.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {activityClass.description}
        </Link>
      </b>
      {!!activityClass.legacy_id && (
        <div className={classes.muted}>Id legado {activityClass.legacy_id}</div>
      )}
    </div>
  )
}

const ClassMobileCell = ({ activityClass }) => {
  const classes = useStyles()
  return (
    <div>
      <b>{activityClass.description}</b>
      <div className={classes.muted}>
        {[
          activityClass.activity_modality,
          formatWeekDays(activityClass.week_days),
        ]
          .filter(Boolean)
          .join(' · ')}
      </div>
    </div>
  )
}

const ActivityClassListPage = () => {
  const showModal = useModal()
  const dismissModal = useDismissModal()

  const printAttendance = (selected, description) =>
    showModal({
      title: 'Imprimir chamadas',
      children: (
        <AttendanceCycleForm
          description={description}
          onConfirm={(cycle) => {
            dismissModal()
            printAttendanceList({ cycle, selected })
          }}
        />
      ),
    })

  const openRollCall = (row) =>
    showModal({
      title: `Chamada · ${row.description}`,
      children: <ActivityClassRollCall classId={row.id} />,
    })

  const describe = (rows) =>
    rows.length === 1
      ? `Chamada da turma ${rows[0].description}.`
      : `Chamadas de ${rows.length} turmas.`

  return (
    <ModelListPage
      djangoApp="activities"
      djangoModel="activity-class"
      title="Turmas"
      countLabel={{ singular: 'turma', plural: 'turmas' }}
      orderBy="description"
      baseParams={{ is_bookable_class: false }}
      searchPlaceholder="Buscar turmas…"
      emptyStateText="Nenhuma turma encontrada"
      filters={ACTIVITY_CLASS_FILTERS}
      headerActions={[
        {
          key: 'print-all',
          label: 'Imprimir chamadas de todas as turmas',
          icon: 'print',
          onClick: () => printAttendance('all', 'Chamadas de todas as turmas.'),
        },
        {
          key: 'report',
          label: 'Relatório de alunos das turmas',
          icon: 'content_paste_search',
          to: '/custom-report?app=activities&model=activity-class-user',
        },
      ]}
      selection={{
        actions: [
          {
            key: 'print',
            label: 'Imprimir chamadas',
            icon: 'print',
            onClick: (rows) =>
              printAttendance(
                rows.map(({ id }) => id),
                describe(rows)
              ),
          },
        ],
      }}
      rowActions={(row) => [
        {
          key: 'details',
          label: 'Ver turma',
          icon: 'groups',
          to: `/activity-class-details/${row.id}`,
        },
        {
          key: 'roll-call',
          label: 'Fazer chamada',
          icon: 'fact_check',
          onClick: () => openRollCall(row),
        },
        {
          key: 'print',
          label: 'Imprimir chamada',
          icon: 'print',
          onClick: () => printAttendance([row.id], describe([row])),
        },
      ]}
      columns={[
        {
          key: 'description',
          header: 'Turma',
          mobile: 'primary',
          render: (row) => <ClassCell activityClass={row} />,
          renderMobile: (row) => <ClassMobileCell activityClass={row} />,
        },
        {
          key: 'activity_modality',
          header: 'Modalidade',
          mobile: false,
          render: (row) => row.activity_modality,
        },
        { key: 'teacher', header: 'Professor', render: (row) => row.teacher },
        {
          key: 'week_days',
          header: 'Dias',
          mobile: false,
          render: (row) => formatWeekDays(row.week_days),
        },
        {
          key: 'physical_space',
          header: 'Espaço',
          render: (row) => row.physical_space,
        },
        {
          key: 'max_number_of_users',
          header: 'Vagas',
          align: 'right',
          width: 90,
          render: (row) => row.max_number_of_users,
        },
      ]}
    />
  )
}

export default ActivityClassListPage
